import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

const LegalPage = ({ title, lastUpdated, children, testId }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div style={{ background: 'var(--sb-bg-3)', minHeight: '100vh', color: 'var(--sb-text-1)' }} data-testid={testId}>
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{ background: 'rgba(0,0,0,0.92)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm">
            <ArrowLeft size={16} />
            SwarmBase
          </Link>
          <span className="font-outfit font-black text-white text-sm">SWARM<span className="text-[#30d5c8]">BASE</span></span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <div className="mb-10">
          <div className="section-divider" />
          <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-3">Legal</p>
          <h1 className="font-outfit font-black text-white text-3xl md:text-4xl tracking-tight mb-3">{title}</h1>
          <p className="text-white/35 text-xs font-mono">Last Updated: {lastUpdated}</p>
        </div>
        <div className="flex flex-col gap-8">{children}</div>
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/25 text-xs font-mono">© 2026 SwarmBase. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy" className="text-white/40 hover:text-[#30d5c8] text-xs transition-colors">Privacy</Link>
            <Link to="/terms" className="text-white/40 hover:text-[#30d5c8] text-xs transition-colors">Terms</Link>
            <Link to="/disclaimer" className="text-white/40 hover:text-[#30d5c8] text-xs transition-colors">Disclaimer</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

const Section = ({ number, title, children }) => (
  <section data-testid={`disclaimer-section-${number}`}>
    <h2 className="font-outfit font-bold text-white text-lg mb-3">{number}. {title}</h2>
    <div className="text-white/60 text-sm leading-relaxed flex flex-col gap-2">{children}</div>
  </section>
);

const Disclaimer = () => (
  <LegalPage title="Disclaimer" lastUpdated="February 2026" testId="disclaimer-page">
    {/* Warning banner */}
    <div className="glass rounded-xl p-5 border border-yellow-500/25 flex gap-3" data-testid="disclaimer-warning-banner">
      <AlertTriangle size={18} className="text-yellow-400 flex-shrink-0 mt-0.5" />
      <p className="text-yellow-400/80 text-sm leading-relaxed">
        <strong className="text-yellow-400">Important:</strong> Please read this disclaimer carefully before using SwarmBase or purchasing $SWARM tokens. Cryptocurrency investments involve significant risk of loss.
      </p>
    </div>

    <Section number="1" title="Not Financial Advice">
      <p>Nothing on swarmbase.io, in this whitepaper, in our documentation, in our communications, or in any related materials constitutes financial, investment, legal, tax, or professional advice of any kind. All content is provided for informational purposes only.</p>
      <p>SwarmBase does not recommend that any person buy, sell, or hold $SWARM or any other cryptocurrency. You should conduct your own due diligence and consult with qualified financial and legal advisors before making any investment decisions.</p>
    </Section>

    <Section number="2" title="Tokens Are Not Securities">
      <p>$SWARM is a utility token designed to provide access to the SwarmBase protocol's compute and coordination services. $SWARM is NOT a security, NOT an investment contract, NOT a share of equity, and NOT a debt instrument.</p>
      <p>SwarmBase has made good-faith efforts to structure $SWARM as a utility token. However, the regulatory classification of digital tokens is uncertain and subject to change. SwarmBase makes no representation that $SWARM will not be classified as a security in any jurisdiction.</p>
      <p>Token value may go to zero. Past performance of any token or protocol does not predict future results.</p>
    </Section>

    <Section number="3" title="Forward-Looking Statements">
      <p>This website, our whitepaper, and all related materials contain forward-looking statements, including but not limited to: roadmap milestones, technical capabilities, token utility projections, adoption estimates, and market opportunity assessments.</p>
      <p>These statements involve known and unknown risks and uncertainties that may cause actual results, performance, or achievements to differ materially from those expressed or implied. SwarmBase undertakes no obligation to update forward-looking statements except as required by law.</p>
    </Section>

    <Section number="4" title="Smart Contract Risk">
      <p>Using SwarmBase involves direct interaction with smart contracts deployed on public blockchains. Smart contracts contain code that may have bugs, vulnerabilities, or unintended behaviors that could result in the permanent loss of funds or tokens.</p>
      <p>SwarmBase undergoes third-party security audits; however, audits do not guarantee the absence of vulnerabilities. You should only interact with SwarmBase smart contracts if you understand these risks and have independently reviewed the protocol code.</p>
    </Section>

    <Section number="5" title="Technology Risk">
      <p>SwarmBase relies on experimental and rapidly evolving technologies, including distributed systems, cryptographic protocols, and AI infrastructure. These technologies may fail to work as intended, may become obsolete, or may be rendered uncompetitive by superior alternatives.</p>
    </Section>

    <Section number="6" title="Assumption of Risk">
      <p>BY USING SWARMBASE OR ACQUIRING $SWARM, YOU EXPRESSLY ACKNOWLEDGE AND ACCEPT THAT: (1) cryptocurrency investments are speculative and highly volatile; (2) you may lose your entire investment; (3) you have conducted your own independent research; (4) you are not relying on SwarmBase for investment advice; and (5) you are solely responsible for your investment decisions.</p>
    </Section>

    <Section number="7" title="No Guarantees">
      <p>SwarmBase makes no guarantees regarding: the future value of $SWARM; the timely delivery of roadmap milestones; the security of the protocol; the availability of the Services; regulatory approval in any jurisdiction; or the commercial success of the protocol.</p>
    </Section>

    <Section number="8" title="Contact">
      <p>For legal inquiries related to this disclaimer: <strong className="text-[#30d5c8]">legal@swarmbase.io</strong></p>
      <p>SwarmBase — Registered in the British Virgin Islands.</p>
    </Section>
  </LegalPage>
);

export default Disclaimer;
